/**
 * DataSectionWrapper — renders a section title/description above data content
 * and a "Show More" button below it that navigates to the full Public Page
 * (/p/:tableSlug) for the bound table.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

const ALIGN_FLEX = { left: 'flex-start', center: 'center', right: 'flex-end' };
const ALIGN_TEXT = { left: 'left',       center: 'center', right: 'right'   };

function showMoreBtnStyle(cfg) {
  const color  = cfg.show_more_color || 'var(--primary-color)';
  const style_ = cfg.show_more_style || 'outline';
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '0.55rem 1.75rem', borderRadius: 8, fontWeight: 600,
    fontSize: '0.9rem', cursor: 'pointer', border: 'none',
    transition: 'opacity 0.15s, transform 0.15s', textDecoration: 'none',
    fontFamily: 'inherit',
  };
  switch (style_) {
    case 'filled':
      return { ...base, background: color, color: '#fff' };
    case 'ghost':
      return { ...base, background: 'transparent', color: color, padding: '0.4rem 1rem', textDecoration: 'underline' };
    default: // outline
      return { ...base, background: 'transparent', color: color, border: `2px solid ${color}` };
  }
}

export default function DataSectionWrapper({ cfg, dataBinding, children }) {
  const navigate   = useNavigate();
  const cfg_       = cfg || {};
  const tableSlug  = dataBinding?.table_slug;

  const hasHeading = cfg_.section_title || cfg_.section_description;
  const showMore   = cfg_.show_more !== false && Boolean(tableSlug);
  const headingAlign = cfg_.heading_align || 'left';

  return (
    <div>
      {/* ── Section heading ─────────────────────────────────────────────── */}
      {hasHeading && (
        <div style={{ marginBottom: '1.5rem', textAlign: ALIGN_TEXT[headingAlign] }}>
          {cfg_.section_title && (
            <h2
              style={{
                fontWeight: 700,
                fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                color: cfg_.title_color || 'inherit',
                marginBottom: cfg_.section_description ? '0.5rem' : 0,
              }}
            >
              {cfg_.section_title}
            </h2>
          )}
          {cfg_.section_description && (
            <p
              style={{
                color: cfg_.description_color || '#64748b',
                fontSize: '1rem',
                margin: 0,
                maxWidth: headingAlign === 'center' ? 640 : '100%',
                marginLeft:  headingAlign === 'center' ? 'auto' : undefined,
                marginRight: headingAlign === 'center' ? 'auto' : undefined,
              }}
            >
              {cfg_.section_description}
            </p>
          )}
        </div>
      )}

      {/* ── Data content ────────────────────────────────────────────────── */}
      {children}

      {/* ── Show More button ─────────────────────────────────────────────── */}
      {showMore && (
        <div
          style={{
            display: 'flex',
            justifyContent: ALIGN_FLEX[cfg_.show_more_align || 'center'],
            marginTop: '2rem',
          }}
        >
          <button
            type="button"
            style={showMoreBtnStyle(cfg_)}
            onClick={() => navigate(`/p/${tableSlug}`)}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1';    e.currentTarget.style.transform = 'translateY(0)';    }}
          >
            {cfg_.show_more_text || 'View All'}
            <i className="bi bi-arrow-right" />
          </button>
        </div>
      )}
    </div>
  );
}
